import { useState } from 'react';
import { useCreateLead } from '@/hooks/useLeadsOperations';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { validateWorkspaceForCreate } from '@/lib/workspaceValidation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CreateLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SOURCES = ['manual', 'meta', 'google', 'linkedin', 'website', 'whatsapp', 'other'] as const;
const TEMPERATURES = ['hot', 'warm', 'cold'] as const;
const STAGES = ['new', 'contacted', 'appointment', 'proposal', 'won', 'lost'] as const;

export function CreateLeadModal({ open, onOpenChange }: CreateLeadModalProps) {
  const createLead = useCreateLead();
  const { activeWorkspaceId, mode } = useWorkspaceScope();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    country_code: '',
    source: 'manual' as typeof SOURCES[number],
    temperature: 'hot' as typeof TEMPERATURES[number], // New leads start as HOT
    pipeline_stage: 'new' as typeof STAGES[number],
    notes_short: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    // Validate workspace before creating lead
    const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'create a lead');
    if (!validation.isValid) {
      return; // Toast already shown by validateWorkspaceForCreate
    }

    await createLead.mutateAsync({
      name: formData.name.trim(),
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      source: formData.source,
      temperature: formData.temperature,
      pipeline_stage: formData.pipeline_stage,
      notes_short: formData.notes_short.trim() || null,
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      title: '',
      country_code: '',
      source: 'manual',
      temperature: 'hot', // Reset to HOT for new lead
      pipeline_stage: 'new',
      notes_short: '',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Lead</DialogTitle>
        </DialogHeader>
        {(!activeWorkspaceId || mode === 'all') && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {mode === 'all' 
                ? 'Cannot create leads in "All" mode. Please select a specific workspace.'
                : 'No active workspace. Please select a workspace first.'}
            </AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                required
                disabled={!activeWorkspaceId || mode === 'all'}
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 555 123 4567"
              />
            </div>

            <div>
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company name"
              />
            </div>

            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Job title"
              />
            </div>

            <div>
              <Label htmlFor="source">Source</Label>
              <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v as typeof formData.source })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="temperature">Temperature</Label>
              <Select value={formData.temperature} onValueChange={(v) => setFormData({ ...formData, temperature: v as typeof formData.temperature })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPERATURES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="stage">Stage</Label>
              <Select value={formData.pipeline_stage} onValueChange={(v) => setFormData({ ...formData, pipeline_stage: v as typeof formData.pipeline_stage })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes_short}
                onChange={(e) => setFormData({ ...formData, notes_short: e.target.value })}
                placeholder="Quick notes about this lead..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createLead.isPending || !formData.name.trim() || !activeWorkspaceId || mode === 'all'}>
              {createLead.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
